import React, { useState } from "react";
import { Typography, Space, Flex } from "antd";
import { SafetyCertificateOutlined, StopOutlined } from "@ant-design/icons";
import SbModal from "uiCore/SbModal/SbModal";
import SbButton, { BUTTON_TYPE } from "uiCore/SbButton/SbButton";
import SbCheckbox from "uiCore/SbCheckbox/SbCheckbox";
import SbForm from "uiCore/SbForm/SbForm";
import SbTag from "uiCore/SbTag/SbTag";
import { SbNotification } from "uiCore";
import { saveConsent } from "Api/apiService";
import {
  PRIVACY_TITLE,
  PRIVACY_DESCRIPTION,
  RESEARCH_CONSENT_TEXT,
  TERMS_TAB_NOTE,
  TERMS_VERSION,
} from "constants/legal.constants";
import {
  TermsLinkStyled,
  GeoBoxStyled,
  ConsentCardStyled,
  FootnoteStyled,
  DeclineScreenStyled,
  DeclineIconStyled,
} from "Pages/Legal/Legal.styles";

const { Paragraph, Title, Text } = Typography;

/**
 * LegalConsentModal
 * mode="create" → first-login gate (blocking, no cancel)
 * mode="edit"   → re-open from Legal tab to update preferences
 */
const LegalConsentModal = ({ open, mode = "create", initial = null, onDone, onCancel, onViewTerms }) => {
  const isEdit = mode === "edit";
  const [form] = SbForm.useForm();
  const [declined, setDeclined] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFinish = async ({ privacyAccepted, researchOptIn }) => {
    if (!privacyAccepted) {
      SbNotification.error("Consent required", "Mandatory Privacy Framework consent must be granted.");
      return;
    }
    // Editing with no changes → don't call the API at all.
    const unchanged = isEdit && initial && !!researchOptIn === !!initial.research_opt_in;
    if (unchanged) {
      onCancel?.();
      return;
    }
    setSaving(true);
    try {
      const result = await saveConsent({
        privacy_accepted: privacyAccepted,
        research_opt_in: !!researchOptIn,
        terms_version: TERMS_VERSION,
      });
      SbNotification.success("Compliance signed", "Your consent has been recorded.");
      onDone?.(result);
    } catch (e) {
      SbNotification.error("Save failed", "Could not record your consent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const footer = declined
    ? null
    : [
        isEdit ? (
          <SbButton key="cancel" onClick={onCancel}>Cancel</SbButton>
        ) : (
          <SbButton key="discontinue" onClick={() => setDeclined(true)}>Discontinue Session</SbButton>
        ),
        <SbButton key="ok" buttonType={BUTTON_TYPE.PRIMARY} loading={saving} onClick={form.submit}>
          {isEdit ? "Save Preferences" : "I Agree and Enter Workspace"}
        </SbButton>,
      ];

  return (
    <SbModal
      open={open}
      centered
      title={
        <Space>
          <SafetyCertificateOutlined />
          Platform Terms & Data Protection
        </Space>
      }
      width={640}
      footer={footer}
      closable={isEdit}
      maskClosable={isEdit}
      onCancel={isEdit ? onCancel : undefined}
    >
      {declined ? (
        <DeclineScreenStyled vertical align="center" justify="center" gap={16}>
          <DeclineIconStyled align="center" justify="center">
            <StopOutlined />
          </DeclineIconStyled>
          <Title level={4}>Session Access Discontinued</Title>
          <Paragraph type="secondary">
            To access your workspace, you must accept the End-User Platform Terms & Data Protection Notice.
            Your session has been temporarily suspended.
          </Paragraph>
          <Space>
            <SbButton onClick={() => { window.location.href = "https://www.skillsbridge.ai"; }}>
              Exit Site
            </SbButton>
            <SbButton buttonType={BUTTON_TYPE.PRIMARY} onClick={() => setDeclined(false)}>
              Review and Consent
            </SbButton>
          </Space>
        </DeclineScreenStyled>
      ) : (
        <SbForm
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            researchOptIn: initial?.research_opt_in || false,
            privacyAccepted: true,
          }}
        >
          <Paragraph type="secondary">
            Welcome to SkillsBridge.AI. To activate your account, please review and agree to our
            terms of use and data handling policies.
          </Paragraph>
          <Paragraph type="secondary">
            By checking the required privacy checkbox below and clicking{" "}
            <Text strong>"{isEdit ? "Save Preferences" : "I Agree and Enter Workspace"}"</Text>, you
            accept our{" "}
            <TermsLinkStyled onClick={onViewTerms}>
              End-User Platform Terms & Data Protection Notice
            </TermsLinkStyled>
            .
          </Paragraph>
          <Paragraph type="secondary">
            We process your professional profile data and platform usage to map workforce skills,
            analyze task alignment, and suggest development pathways in accordance with your
            organization&apos;s subscription.
          </Paragraph>

          <GeoBoxStyled vertical gap={12}>
            <ConsentCardStyled>
              <SbForm.Item name="privacyAccepted" valuePropName="checked" noStyle>
                <SbCheckbox>
                  <Flex vertical gap={4}>
                    <Flex align="center" gap="small">
                      <Text strong>{PRIVACY_TITLE}</Text>
                      <SbTag color="error">Required</SbTag>
                    </Flex>
                    <Text type="secondary">{PRIVACY_DESCRIPTION}</Text>
                  </Flex>
                </SbCheckbox>
              </SbForm.Item>
            </ConsentCardStyled>

            <ConsentCardStyled>
              <SbForm.Item name="researchOptIn" valuePropName="checked" noStyle>
                <SbCheckbox>
                  <Flex vertical gap={4}>
                    <Flex align="center" gap="small">
                      <Text strong>Research Participation & Data Linking Consent</Text>
                      <SbTag>Optional</SbTag>
                    </Flex>
                    <Text type="secondary">{RESEARCH_CONSENT_TEXT}</Text>
                  </Flex>
                </SbCheckbox>
              </SbForm.Item>
            </ConsentCardStyled>
          </GeoBoxStyled>

          <FootnoteStyled>{TERMS_TAB_NOTE}</FootnoteStyled>
        </SbForm>
      )}
    </SbModal>
  );
};

export default LegalConsentModal;
